import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Clipboard,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import BackArrow from '@assets/svg/BackArrow';
import AmountWithdrawal from '@assets/svg/Wallet/AmountWithdrawal';
import TxnSuccess from '@assets/svg/Wallet/TxnSuccess';
import TxnPending from '@assets/svg/Wallet/TxnPending';
import TxnFailed from '@assets/svg/Wallet/TxnFailed';
import CopyIcon from '@assets/svg/Wallet/CopyIcon';
import { ScaledSheet } from '@utils/scale';
import { getWalletTransactionDetails } from '@services/api';
import Timeline from './Timeline';

export default function WithdrawalTxnDetails({ route }) {
  const navigation = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const transactionId = route?.params?.transactionId;

  useFocusEffect(
    useCallback(() => {
      fetchTransactionDetails();
    }, [transactionId])
  );

  const fetchTransactionDetails = async () => {
    setIsLoading(true);
    try {
      const { data } = await getWalletTransactionDetails(transactionId);
      setData(data);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimelineData = (statusLines) => {
    if (!statusLines || statusLines.length === 0) return [];
    
    const data = statusLines.map((step, index) => {
      // Always use TxnSuccess for the first item (index 0)
      const isFirstItem = index === 0;
      let Icon = isFirstItem ? TxnSuccess : null;
      
      if (!isFirstItem) {
        switch (step.status) {
          case 'PROCESSED':
            Icon = TxnSuccess;
            break;
          case 'FAILED':
            Icon = TxnFailed;
            break;
          case 'PROCESSING':
            Icon = TxnPending;
            break;
          default:
            Icon = null;
        }
      }

      return {
        id: index + 1,
        title: step.title || '',
        subtitle: step.subtitle || '',
        description: step.description || '',
        Icon: Icon,
      };
    });
    return data;
  };

  const getVerticalLineColor = (statusLines) => {
    if (!statusLines || statusLines.length === 0) return '#616161';

    // Check the status of the last element
    const lastStatus = statusLines[statusLines.length - 1].status;
    const lineColor = lastStatus === 'PROCESSED' ? '#10B981' : '#D3D3D3';
    return lineColor;
  };

  const copyToClipboard = (text, label) => {
    Clipboard.setString(text);
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <BackArrow />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Transaction Details</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#10B981" />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <BackArrow />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Transaction Details</Text>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Amount Section */}
        <View style={styles.amountSection}>
          <View style={styles.amountFullRow}>
            <Text style={styles.transactionType}>{data?.title}</Text>
            <View style={styles.amountRow}>
              <Text style={styles.amountText}>{data?.value_txt}</Text>
              <AmountWithdrawal />
            </View>
          </View>
        </View>

        <Timeline
          data={formatTimelineData(data?.transaction_status_lines)}
          lineColor={getVerticalLineColor(data?.transaction_status_lines)}
          iconSize={44}
          onItemPress={() => { }}
          containerStyle={{}}
        />

        {/* Transaction Details Card */}
        <View style={styles.detailsCard}>
          <View style={styles.detailsHeader}>
            <Text style={styles.detailsTitle}>Transaction Details</Text>
            {/* <TouchableOpacity style={styles.raiseTicketButton}>
              <Text style={styles.raiseTicketText}>Raise a ticket</Text>
            </TouchableOpacity> */}
          </View>

          {/* UTR Number */}
          {data?.transaction_details?.utr_no ? (
            <>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>UTR no : </Text>
                <Text style={styles.detailValue}>{data.transaction_details.utr_no}</Text>
                <TouchableOpacity
                  onPress={() =>
                    copyToClipboard(data.transaction_details.utr_no, 'UTR number')
                  }
                  style={styles.copyButton}
                >
                  <CopyIcon />
                </TouchableOpacity>
              </View>
              <View style={styles.divider} />
            </>
          ) : <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>UTR no : </Text>
            <Text style={styles.detailValue}>N/A</Text>
          </View>}

          {/* Bank Account Name */}
          {data?.transaction_details?.bank_account_name && (
            <View style={styles.detailBlock}>
              <Text style={styles.detailLabel}>
                Bank account name : <Text style={styles.detailValueInline}>{data.transaction_details.bank_account_name}</Text>
              </Text>
            </View>
          )}

          {/* Account Number */}
          {data?.transaction_details?.bank_account_no && (
            <View style={styles.detailBlock}>
              <Text style={styles.detailLabel}>
                Account number : <Text style={styles.detailValueInline}>{data.transaction_details.bank_account_no}</Text>
              </Text>
            </View>
          )}

          {/* UPI ID */}
          {data?.transaction_details?.upi_id && (
            <View style={styles.detailBlock}>
              <Text style={styles.detailLabel}>
                UPI ID : <Text style={styles.detailValueInline}>{data.transaction_details.upi_id}</Text>
              </Text>
            </View>
          )}
        </View>

        {/* Bottom Button */}
        <TouchableOpacity
          style={styles.bottomButton}
          onPress={() => navigation.goBack()}
        >
          <Text style={styles.bottomButtonText}>Okay got it</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
}

const styles = ScaledSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    marginLeft: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
  },
  amountSection: {
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  amountFullRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  transactionType: {
    fontSize: 20,
    fontWeight: '400',
    color: '#111827',
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  amountText: {
    fontSize: 20,
    fontWeight: '400',
    color: '#111827',
    marginRight: 8,
  },
  amountIcon: {
    fontSize: 24,
    color: '#EF4444',
  },
  timelineContainer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    width: '100%',
  },
  timelineItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    minHeight: 60,
    width: '100%',
    position: 'relative',
    marginBottom: 4,
    paddingTop: 4, // Add padding to align with text
  },
  timelineIconContainer: {
    position: 'relative',
    width: 32,
    flexShrink: 0,
    marginRight: 16,
    alignItems: 'center',
    minHeight: 32,
    marginTop: 2,
    height: '100%', // Ensure container takes full height
  },
  timelineIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: 2,
    backgroundColor: 'transparent',
  },
  timelineIconText: {
    fontSize: 16,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  timelineLine: {
    position: 'absolute',
    width: 2,
    top: 36, // Start below the icon
    left: 15,
    bottom: 0,
    backgroundColor: '#10B981',
    zIndex: 1,
  },
  timelineLineTop: {
    position: 'absolute',
    width: 2,
    top: 0,
    left: 15,
    height: 16, // Fixed height for the top line
    backgroundColor: '#10B981',
    zIndex: 1,
  },
  timelineContent: {
    flex: 1,
    paddingTop: 0,
    paddingBottom: 8,
    paddingRight: 16,
    justifyContent: 'flex-start',
  },
  timelineTitle: {
    fontSize: 16,
    fontWeight: '400',
    color: '#111827',
    marginBottom: 2,
    flexWrap: 'wrap',
  },
  timelineSubtitle: {
    fontWeight: '400',
    fontSize: 14,
    color: '#4A5565',
    lineHeight: 20,
    flexWrap: 'wrap',
  },
  timelineDescription: {
    fontSize: 13,
    color: '#9CA3AF',
    marginTop: 4,
    flexWrap: 'wrap',
  },
  detailsCard: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    paddingTop: 16,
    paddingHorizontal: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  detailsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  detailsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  raiseTicketButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#111827',
  },
  raiseTicketText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 8,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  detailLabel: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  detailValueLarge: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginTop: 4,
  },
  detailValueInline: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  copyButton: {
    padding: 4,
    marginLeft: 8,
  },
  copyIcon: {
    fontSize: 16,
  },
  detailBlock: {
    marginTop: 8,
    marginBottom: 16,
  },
  bottomButton: {
    backgroundColor: '#1F2937',
    marginHorizontal: 20,
    marginTop: 32,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  bottomButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
